import { EventEmitter } from '@angular/core';
import { MatSnackBar, MatSnackBarDismiss } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { from, Observable, of, Subscription } from 'rxjs';
import { filter, finalize, switchMap, tap } from 'rxjs/operators';
import { ArchiveStatus } from 'src/app/billers/models/archive-status';
import { DelegationDataType } from 'src/app/shared/models/delegation-data-type.enum';
import { LiabilitySource } from '../../billers/models/model';
import { ManualStatusEdition } from '../../core/models/status-change-source.enum';
import {
  HideLiabilityConfirmationDialogComponent,
} from '../components/hide-liability-confirmation-dialog/hide-liability-confirmation-dialog.component';
import { Liability } from '../models/liability.model';
import { LiabilityMenuDisabledState, LiabilityStatus } from '../models/model';
import { XsellService } from '../../billers/services/xsell.service';
import { AnalyticsEvent, AnalyticsService } from '../../core/services/analytics-service';
import { DialogService } from '../../core/services/dialog.service';
import { AddToCartEventSource } from '../../pay/models/add-to-cart-event-source.enum';
import { PaymentService } from '../../pay/services/payment.service';
import { LiabilityService } from '../services/liability.service';
import { TypeformService } from 'src/app/shared/services/typeform.service';
import {
  FillAmountDialogComponent,
  FillAmountDialogResult,
} from '../components/fill-amount-dialog/fill-amount-dialog.component';
import { LiabilityApiService } from '../services/liability-api.service';
import { LiabilityItemService } from '../services/liability-item.service';
import { AppConfig } from '../../core/services/app-config.service';
import { CartStoreService } from '../../shared/services/cart.service';

export abstract class LiabilityActions {
  public showAddToCart = false;
  public showEdit = false;
  public showRedirectToCart = false;
  public showMarkAsPaid = false;
  public showMarkAsNotPaid = false;
  public showRemove = false;
  public showRemoveFromCart = false;
  public showEnableAutoPayment = false;
  public showCreateAutoPayment = false;
  public showCancelAutoPayment = false;
  public showDownloadPdf = false;
  public showDetails = false;
  public showActionsMenu = false;
  public showXsellOffer = false;
  public showDelegate = false;
  public showDeleteDelegation = false;
  public liabilityMarkedAsPaidEvent = new EventEmitter<void>();
  public canBeReminded = false;

  protected startedPaymentId: string;
  protected liability: Liability;
  protected route: ActivatedRoute;
  protected checklistSource: boolean;

  private liabilityService: LiabilityService;
  private paymentService: PaymentService;
  private analyticsService: AnalyticsService;
  private xsellService: XsellService;
  private dialog: DialogService;
  private snackbar: MatSnackBar;
  private router: Router;

  private addToCartStatuses: Array<LiabilityStatus> = [
    LiabilityStatus.NOT_PAID,
    LiabilityStatus.OVERDUE,
    LiabilityStatus.SCHEDULED_NOT_PAID,
    LiabilityStatus.UNKNOWN,
  ];
  private markAsPaidStatuses: Array<LiabilityStatus> = [
    LiabilityStatus.NOT_PAID,
    LiabilityStatus.OVERDUE,
    LiabilityStatus.UNKNOWN,
    LiabilityStatus.IN_VERIFICATION,
    LiabilityStatus.NOT_VERIFIED,
  ];
  private markAsNotPaidStatuses: Array<LiabilityStatus> = [
    LiabilityStatus.PAID_OUTSIDE_PLATFORM,
    LiabilityStatus.UNKNOWN,
  ];
  private removeStatuses: Array<LiabilityStatus> = [
    LiabilityStatus.NOT_PAID,
    LiabilityStatus.PAID_OUTSIDE_PLATFORM,
    LiabilityStatus.OVERDUE,
    LiabilityStatus.UNKNOWN,
    LiabilityStatus.IN_VERIFICATION,
    LiabilityStatus.NOT_VERIFIED,
  ];

  constructor(
    liabilityService: LiabilityService,
    paymentService: PaymentService,
    analyticsService: AnalyticsService,
    xsellService: XsellService,
    dialog: DialogService,
    snackbar: MatSnackBar,
    router: Router,
    route: ActivatedRoute,
    private typeformService: TypeformService,
    private liabilityApiService: LiabilityApiService,
    public liabilityItemService: LiabilityItemService,
    public appConfig: AppConfig,
    private cartStoreService: CartStoreService,
  ) {
    this.liabilityService = liabilityService;
    this.paymentService = paymentService;
    this.analyticsService = analyticsService;
    this.xsellService = xsellService;
    this.dialog = dialog;
    this.snackbar = snackbar;
    this.router = router;
    this.route = route;
  }

  public calculateActions(): void {
    this.showAddToCart = this.canShowAddToCart();
    this.showEdit = this.canShowEdit();
    this.showRedirectToCart = this.canShowRedirectToCart();
    this.showMarkAsPaid = this.canShowMarkAsPaid();
    this.showMarkAsNotPaid = this.canShowMarkAsNotPaid();
    this.showRemove = this.canShowRemove();
    this.showRemoveFromCart = this.canShowRemoveFromCart();
    this.showEnableAutoPayment = this.canShowEnableAutoPayment();
    this.showCreateAutoPayment = this.canShowCreateAutoPayment();
    this.showCancelAutoPayment = this.canShowCancelAutoPayment();
    this.showDownloadPdf = this.canShowDownloadPdf();
    this.showDetails = this.canShowDetails();
    this.showActionsMenu = this.canShowActionsMenu();
    this.showXsellOffer = this.canShowXsellOffer();
    this.showDelegate = this.canShowDelegate();
    this.showDeleteDelegation = this.canShowDeleteDelegation();
    this.canBeReminded = this.liability.canBeReminded;
  }

  public abstract canShowDetails(): boolean;

  public handleActionWithDisabled(action$: Observable<any>): Observable<any> {
    this.liabilityItemService.changeLiabilityItemMenuDisabled(LiabilityMenuDisabledState.DISABLED);
    return action$.pipe(
      finalize(() => {
        this.liabilityItemService.changeLiabilityItemMenuDisabled(LiabilityMenuDisabledState.ENABLED);
      }),
    );
  }

  public initPayment = (paymentSource: AddToCartEventSource) => {
    if (!this.liability?.amount) {
      return this.handleActionWithDisabled(of(this.fillAmount(paymentSource)));
    }
    if (this.startedPaymentId) {
      return this.handleActionWithDisabled(
        from(
          this.router.navigate(['otherPaymentInfo'], {
            queryParams: { startedPaymentId: this.startedPaymentId },
            relativeTo: this.route,
          }),
        ),
      );
    } else {
      return this.paymentService.findPaymentIdByLiabilityId(this.liability.id).pipe(
        tap((response) => {
          if (response.paymentId) {
            // this.goToPayment(response.paymentId);
          } else {
            this.handleActionWithDisabled(
              this.paymentService.initPayment([this.liability.id], paymentSource),
            ).subscribe(() => this.trackStartedPaymentEvent());
          }
        }),
      );
    }
  };

  public hide = () =>
    this.dialog
      .open(HideLiabilityConfirmationDialogComponent, {
        closeOnNavigation: true,
        data: this.liability,
        disableClose: true,
        maxWidth: 400,
      })
      .afterClosed()
      .pipe(
        filter((result) => result),
        tap(() => this.analyticsService.trackEvent(new AnalyticsEvent('Hideliability'))),
        switchMap(() => this.liabilityService.hide(this.liability.id)),
        switchMap(() => this.router.navigate(['/liabilities'])),
      );

  public edit = () => this.liabilityService.edit(this.liability);

  public markAsPaid = () =>
    this.handleActionWithDisabled(
      this.liabilityService.markAsPaid(this.liability.id)
        .pipe(
          tap(() => this.liabilityMarkedAsPaidEvent.emit()),
          tap(() => this.cartStoreService.removeId(this.liability.id)),
        ),
    );

  public markAsNotPaid = () => this.handleActionWithDisabled(this.liabilityService.markAsNotPaid(this.liability.id));

  public downloadPdf = () => this.liabilityService.downloadPdf(this.liability.id);

  public createAutoPayment = () =>
    this.handleActionWithDisabled(
      from(this.router.navigateByUrl(`/billers/${this.liability.customerBillerId}/auto-payment/configure`)),
    );

  public enableAutoPayment = () =>
    this.handleActionWithDisabled(this.liabilityService.enableAutoPayment(this.liability));

  public disableAutoPayment = () =>
    this.handleActionWithDisabled(
      this.liabilityService.disableWithConfirmationDialog(this.liability).pipe(
        tap((liablity) => {
          this.liability = liablity;
          this.calculateActions();
        }),
      ),
    );

  public cancelPayment = () =>
    this.handleActionWithDisabled(
      this.paymentService.recreatePayment(this.liability.uncompletedPaymentId, {
        removedLiabilityIds: [this.liability.id],
      }),
    );

  public redirectToXsellOffer = () =>
    this.xsellService.showXsellConsentAdditionalInfo(
      this.liability.customerBillerId,
      this.route,
      'liability actions menu',
    );

  public delegateLiability = () => of(this.router.navigateByUrl(`/liabilities/${this.liability.id}/delegate`));

  public deleteLiabilityDelegation = () => this.liabilityService.deleteLiabilityDelegation(this.liability);

  public remindAboutPayment = () => this.liabilityService.remindAboutPayment(this.liability);

  public reportBtApiIssue() {
    this.typeformService.showReportBtApiIssue(this.liability.btApiAccountId, this.liability.billerCode, this.liability.title);
  }

  private canShowAddToCart(): boolean {
    return (
      this.addToCartStatuses.includes(this.liability.status) && this.liability.verified &&
      (this.liability.payable || (!this.liability.payable && !this.liability.amount)) &&
      this.liability.customerBillerArchiveStatus === ArchiveStatus.UNARCHIVED
    );
  }

  private canShowEdit(): boolean {
    var notPaidStatuses = [LiabilityStatus.NOT_PAID, LiabilityStatus.OVERDUE,
      LiabilityStatus.IN_VERIFICATION, LiabilityStatus.NOT_VERIFIED];
    var notEbokSources = [LiabilitySource.EMAIL_FORWARD, LiabilitySource.MANUAL_SCANNING,
      LiabilitySource.TOP_UP, LiabilitySource.MANUAL];
    return (notEbokSources.includes(this.liability.source) && !this.liability.inCart &&
      this.liability.customerBillerArchiveStatus === ArchiveStatus.UNARCHIVED &&
      notPaidStatuses.includes(this.liability.status));
  }

  private canShowRedirectToCart(): boolean {
    return (
      LiabilityStatus.PENDING_PAYMENT === this.liability.status &&
      this.liability.customerBillerArchiveStatus === ArchiveStatus.UNARCHIVED &&
      this.liability.uncompletedPaymentId &&
      !this.startedPaymentId
    );
  }

  private canShowMarkAsPaid(): boolean {
    return (
      this.markAsPaidStatuses.includes(this.liability.status) &&
      !this.liability.inCart &&
      this.liability.manualStatusEdition === ManualStatusEdition.ENABLED &&
      this.checkTopUpAndArchive()
    );
  }

  private canShowMarkAsNotPaid(): boolean {
    return (
      this.markAsNotPaidStatuses.includes(this.liability.status) &&
      !this.liability.inCart &&
      this.liability.manualStatusEdition === ManualStatusEdition.ENABLED &&
      this.checkTopUpAndArchive() &&
      !!this.liability.bban
    );
  }

  private canShowRemove(): boolean {
    return (
      this.removeStatuses.includes(this.liability.status) &&
      this.liability.source !== LiabilitySource.USERPANEL_SCRAPER &&
      this.liability.customerBillerArchiveStatus === ArchiveStatus.UNARCHIVED &&
      !this.liability.inCart
    );
  }

  private canShowRemoveFromCart(): boolean {
    return (
      this.liability.inCart &&
      !!this.liability.uncompletedPaymentId &&
      this.liability.customerBillerArchiveStatus === ArchiveStatus.UNARCHIVED
    );
  }

  private canShowEnableAutoPayment(): boolean {
    return (
      this.liability.automaticPayment.automaticPaymentAvailable &&
      this.liability.automaticPayment.singleAutomaticPaymentAvailable &&
      this.liability.customerBillerArchiveStatus === ArchiveStatus.UNARCHIVED
    );
  }

  private canShowCreateAutoPayment(): boolean {
    return (
      this.liability.automaticPayment.automaticPaymentAvailable &&
      !this.liability.automaticPayment.billerAutomaticPaymentSetup &&
      this.liability.status === LiabilityStatus.NOT_PAID &&
      this.liability.customerBillerArchiveStatus === ArchiveStatus.UNARCHIVED &&
      !this.liability.inCart
    );
  }

  private canShowCancelAutoPayment(): boolean {
    return (
      this.liability.automaticPayment.automaticPaymentId &&
      this.liability.automaticPayment.attemptsLeft > 0 &&
      this.liability.customerBillerArchiveStatus === ArchiveStatus.UNARCHIVED &&
      (this.liability.status === LiabilityStatus.NOT_PAID || this.liability.status === LiabilityStatus.UNKNOWN)
    );
  }

  private canShowDownloadPdf(): boolean {
    return this.liability.hasDocuments;
  }

  private canShowActionsMenu(): boolean {
    return (
      this.showAddToCart ||
      this.showEdit ||
      this.showRedirectToCart ||
      this.showMarkAsPaid ||
      this.showMarkAsNotPaid ||
      this.showRemove ||
      this.showRemoveFromCart ||
      this.showEnableAutoPayment ||
      this.showCreateAutoPayment ||
      this.showCancelAutoPayment ||
      this.showDownloadPdf ||
      this.showDetails
    );
  }

  private canShowXsellOffer(): boolean {
    return this.xsellService.xsellOfferVisibleForLiability(this.liability);
  }

  private canShowDelegate(): boolean {
    return this.liability.delegationData.canBeDelegated;
  }

  private canShowDeleteDelegation(): boolean {
    return (
      !!this.liability.delegationData.targetEmail &&
      this.liability.delegationData.type === DelegationDataType.DELEGATION_INVITATION
    );
  }

  private trackStartedPaymentEvent(): void {
    this.analyticsService.trackEvent(new AnalyticsEvent('START_PAYMENT', { fromChecklist: this.checklistSource }));
  }

  private showSnackbarForRemovedLiability(): Observable<MatSnackBarDismiss> {
    return this.snackbar
      .open(`Usunięto ${this.liability.billerName} ${this.liability.title}`, 'COFNIJ', { duration: 10000 })
      .afterDismissed()
      .pipe(tap(() => this.analyticsService.trackEvent(new AnalyticsEvent('Showliability'))));
  }

  private checkTopUpAndArchive(): boolean {
    return (
      this.liability.source !== null &&
      this.liability.source !== LiabilitySource.TOP_UP &&
      this.liability.customerBillerArchiveStatus === ArchiveStatus.UNARCHIVED
    );
  }

  private fillAmount = (paymentSource: AddToCartEventSource): Subscription =>
    this.dialog
      .open(FillAmountDialogComponent, {
        closeOnNavigation: true,
        data: this.liability,
        disableClose: true,
      })
      .afterClosed()
      .pipe(
        filter((result) => !!result),
        tap(() => this.analyticsService.trackEvent(new AnalyticsEvent('Editliabilityamount'))),
        switchMap((result: FillAmountDialogResult) =>
          this.liabilityApiService.updateAmount(this.liability.id, result.amount),
        ),
        switchMap(() => this.initPayment(paymentSource)),
      )
      .subscribe();
}
